/**
 * GroomerAssign — admin Assign-Groomer picker (#7).
 *
 * A grooming booking has no groomer by default. A manager / receptionist uses
 * this control to assign the booking to a groomer, which writes the
 * BookingResource assignment (POST /api/v2/admin/calendar/assign) and — via the
 * server's groomer denormalisation — populates that groomer's work queue
 * (GroomerHome).
 *
 * Self-contained:
 *   - Loads the staff roster (adminStaffApi.getStaff) and filters to groomers.
 *   - Shows the currently-assigned groomer (when the booking carries one).
 *   - On select, POSTs the assignment, then calls `onAssigned?.()`.
 *   - Owns its own loading / error / success state; never throws to the page.
 *
 * Grooming assignments require a same-day time window (date + start/end), so
 * the booking's own `start_time` seats the window (the api method defaults the
 * end to start + 60m, and the start to 09:00 when the booking has no time).
 */

import { useCallback, useEffect, useState } from 'react';
import { Alert, Select, Spinner, type SelectOption } from '@ferro/ui';
import {
  adminStaffApi,
  resourceApi,
  type AdminStaffUser,
} from '../../lib/api.js';
import { pickableStaff, staffOptionLabel } from '../../lib/staff-picker.js';

/**
 * Minimal structural view of the booking this control needs. `AdminBooking` is
 * assignable to it (it carries `id` + `date` + `start_time`); `groomer_id` is
 * read defensively because the schedule row may denormalise it even though the
 * base booking type doesn't yet declare it.
 */
export interface GroomerAssignBooking {
  readonly id: string;
  readonly date: string;
  readonly start_time?: string | null;
  readonly groomer_id?: string | null;
}

export interface GroomerAssignProps {
  readonly booking: GroomerAssignBooking;
  readonly apiUrl: string;
  readonly token: string;
  /** Called after a successful assignment (e.g. to refresh the bookings list). */
  readonly onAssigned?: () => void;
}

type RosterState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; groomers: AdminStaffUser[] };

function groomerLabel(g: AdminStaffUser): string {
  const name = `${g.first_name ?? ''} ${g.last_name ?? ''}`.trim();
  return name.length > 0 ? name : g.username;
}

export function GroomerAssign({
  booking,
  apiUrl,
  token,
  onAssigned,
}: GroomerAssignProps): JSX.Element {
  const [roster, setRoster] = useState<RosterState>({ kind: 'loading' });
  // The locally-reflected assignment (seeds from the booking, then tracks the
  // last successful assign so the control shows the new groomer immediately).
  const [assignedId, setAssignedId] = useState<string>(booking.groomer_id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Keep the reflected selection in sync if the booking prop's groomer changes
  // (e.g. parent refresh after assignment elsewhere).
  useEffect(() => {
    setAssignedId(booking.groomer_id ?? '');
  }, [booking.groomer_id]);

  const loadRoster = useCallback(async () => {
    setRoster({ kind: 'loading' });
    const res = await adminStaffApi.getStaff(apiUrl);
    if (res.error || !res.data) {
      setRoster({
        kind: 'error',
        message: res.error ?? 'Unable to load the staff roster.',
      });
      return;
    }
    const groomers = (res.data.staff ?? []).filter((s) => s.role === 'groomer');
    setRoster({ kind: 'ready', groomers });
  }, [apiUrl]);

  useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const handleSelect = async (staffUserId: string): Promise<void> => {
    if (staffUserId.length === 0 || staffUserId === assignedId || saving) return;

    setError(null);
    setSuccess(null);
    setSaving(true);
    const previous = assignedId;
    setAssignedId(staffUserId); // optimistic

    const res = await resourceApi.assignGroomer(apiUrl, token, {
      bookingId: booking.id,
      staffUserId,
      date: booking.date,
      startTime: booking.start_time ?? null,
    });

    setSaving(false);
    if (res.error || !res.data) {
      setAssignedId(previous); // roll back
      setError(res.error ?? 'Failed to assign groomer.');
      return;
    }

    const groomers = roster.kind === 'ready' ? roster.groomers : [];
    const assigned = groomers.find((g) => g.id === staffUserId);
    setSuccess(
      assigned
        ? `Assigned to ${groomerLabel(assigned)}.`
        : 'Groomer assigned.',
    );
    onAssigned?.();
  };

  if (roster.kind === 'loading') {
    return (
      <div className="ferro-field" data-testid={`groomer-assign-${booking.id}`}>
        <span className="ferro-field__label">Groomer</span>
        <Spinner size="sm" label="Loading groomers" />
      </div>
    );
  }

  if (roster.kind === 'error') {
    return (
      <div
        className="ferro-field"
        data-testid={`groomer-assign-${booking.id}`}
        role="alert"
      >
        <Alert variant="error">{roster.message}</Alert>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-btn--sm"
          onClick={() => void loadRoster()}
        >
          Retry
        </button>
      </div>
    );
  }

  // Ted Item 32.1 — offer only groomers still on staff, but keep the groomer
  // already on this booking (marked as departed) so the record isn't blanked.
  const options: SelectOption[] = pickableStaff(roster.groomers, assignedId).map((g) => ({
    value: g.id,
    label: staffOptionLabel(g, groomerLabel(g)),
  }));
  const current = roster.groomers.find((g) => g.id === assignedId) ?? null;

  return (
    <div className="ferro-field" data-testid={`groomer-assign-${booking.id}`}>
      <Select
        label="Groomer"
        name={`groomer-${booking.id}`}
        value={assignedId}
        options={options}
        placeholder={
          options.length === 0 ? 'No groomers on staff' : 'Assign a groomer…'
        }
        disabled={saving || options.length === 0}
        onChange={(e) => void handleSelect(e.target.value)}
        helperText={
          current
            ? `Currently assigned: ${staffOptionLabel(current, groomerLabel(current))}`
            : 'No groomer assigned yet.'
        }
        data-testid={`groomer-assign-select-${booking.id}`}
      />
      {saving ? (
        <span className="ferro-muted" role="status">
          <Spinner size="sm" label="Assigning groomer" /> Assigning…
        </span>
      ) : null}
      {error ? <Alert variant="error">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}
    </div>
  );
}
